import { UpsertCboRecordRequestDto } from '@act/shared/data-transfer-objects';
import { AddNeedResourceDetailsToActionRequest } from '@act/shared/types';
import { cboRecordRequests } from 'redux/requests/cboRecords.requests';
import { makeRequest } from 'redux/utils/apiRequest';
import type { ActionFormResourceDetails } from 'react-components/dialog-forms/components/useNeedActionFormtypes';
import { formatSingleResourceReferral } from './formatSingleResourceReferral.util';
import { shouldPersistCboRecord } from './shouldPersistCboRecord.util';

/**
 * Maps a need-action resource referral row to an upsert body.
 *
 * @param details - Referral form row.
 * @returns Upsert payload for the CBO cache.
 */
function referralToUpsertBody(
  details: ActionFormResourceDetails
): UpsertCboRecordRequestDto {
  return {
    googlePlaceId: details.googlePlaceId ?? null,
    name: details.resourceName,
    contactName: details.contactName || null,
    phoneNumber: details.phoneNumber || null,
    email: details.email || null,
    websiteUrl: details.url || null,
    streetAddress: details.streetAddress || null,
    addressDetails: details.addressDetails || null,
    city: details.city || null,
    state: details.state || null,
    zipcode: details.zipcode || null,
    formattedAddress: details.formattedAddress || null,
    placeTypes: details.placeTypes?.length ? details.placeTypes : null,
    categoryLabel: details.categoryLabel || null,
  };
}

/**
 * Persists resource referrals for a need action: CBO cache for Places/CBO picks, plain rows for custom entry.
 *
 * @param referrals - Resource referral rows from the action form.
 * @param needId - Parent need id (usage is recorded later via {@link recordCboUsageForResourceReferrals}).
 * @returns Referral payloads for the needs API.
 */
export async function persistCboForResourceReferrals(
  referrals: ActionFormResourceDetails[],
  _needId: number
): Promise<AddNeedResourceDetailsToActionRequest[]> {
  const results: AddNeedResourceDetailsToActionRequest[] = [];

  for (const details of referrals) {
    if (details.shouldDelete) {
      continue;
    }

    if (!shouldPersistCboRecord(details)) {
      results.push(formatSingleResourceReferral(details));
      continue;
    }

    const existingCboRecordId = details.cboRecordId?.trim();
    if (existingCboRecordId && !details.googlePlaceId?.trim()) {
      results.push({
        ...formatSingleResourceReferral(details),
        cboRecordId: existingCboRecordId,
      });
      continue;
    }

    const record = await makeRequest(cboRecordRequests.upsertCboRecord, {
      params: null,
      body: referralToUpsertBody(details),
    });

    results.push({
      ...formatSingleResourceReferral(details),
      cboRecordId: record.id,
    });
  }

  return results;
}

/**
 * Records `cbo_usage` rows after a need action exists (requires `actionId`).
 *
 * @param needId - Parent need id.
 * @param actionId - Created or updated action id.
 * @param referrals - Referral payloads that include `cboRecordId` when cached.
 */
export async function recordCboUsageForResourceReferrals(
  needId: number,
  actionId: number,
  referrals: AddNeedResourceDetailsToActionRequest[]
): Promise<void> {
  const withCboId = referrals.filter(
    (referral): referral is AddNeedResourceDetailsToActionRequest & {
      cboRecordId: string;
    } => Boolean(referral.cboRecordId)
  );

  await Promise.all(
    withCboId.map(referral =>
      makeRequest(cboRecordRequests.recordCboUsage, {
        params: { id: referral.cboRecordId },
        body: { needId, actionId },
      })
    )
  );
}
